import { DataHandlerDevice } from "../../datahandlers/DataHandlerDevice"
import {
	Device,
	DeviceBrowser,
	DeviceGroup,
	DeviceRPi,
	StatusBrowserDevice,
	StatusDeviceGroup,
	StatusRPiDevice,
} from "../../entries/Device"

type DeviceStatus<T extends DeviceRPi | DeviceGroup | DeviceBrowser> = T extends DeviceRPi
	? StatusRPiDevice
	: T extends DeviceGroup
	? StatusDeviceGroup
	: T extends DeviceBrowser
	? StatusBrowserDevice
	: never

export class DeviceStatusListener<T extends DeviceRPi | DeviceGroup | DeviceBrowser> {
	device: T

	status: DeviceStatus<T> = $state()
	statusText: string = $state()
	statusColor: string = $state()

	/**
	 * Listens for and saves the device state in a svelte state block for easy reactivity
	 * @param device
	 */
	constructor(device: T) {
		this.device = device
		this.updateState()
	}

	/**
	 * To be used within an effect block to initialize the device listeners.
	 * @returns an unsubscriber function
	 */
	listen = () => {
		const listenerIds: Array<[number, string]> = this.devices.map((dev) => {
			const listenerId = dev.addUpdateListener(() => {
				this.updateState()
			})

			return [dev.id!, listenerId]
		})

		return () => {
			listenerIds.forEach(([devId, listenerId]) => {
				const dev = DataHandlerDevice.get(devId)
				if (dev) {
					dev.removeUpdateListener(listenerId)
				}
			})
		}
	}

	/**
	 * Gets the device status and updates the state, text and color
	 */
	private updateState = () => {
		this.status = this.device.status as DeviceStatus<T>
		const statusDisplay = this.device.getStatus()
		this.statusText = statusDisplay.text
		this.statusColor = statusDisplay.color
	}

	/**
	 * Abstracts the devices into a list so device groups and single devices are handled the same
	 */
	get devices(): Array<Device> {
		if (this.device instanceof DeviceGroup) return this.device.getChildDevices()
		return [this.device]
	}
}
